import { Inject, Injectable, InjectionToken, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IContext, UnleashClient } from 'unleash-proxy-client';

import { Logger } from '~/core/logger.service';

import { ToggleService } from './toggle.service';
import { isPlatformBrowser } from '@angular/common';

export const UNLEASH_URL = new InjectionToken<string>('Unleash Proxy URL');
export const UNLEASH_SECRET = new InjectionToken<string>(
  'Unleash Proxy Secret'
);
export const UNLEASH_ENV = new InjectionToken<string>('Unleash environment');
export const UNLEASH_APP = 'ActivitiesApp';
export const UNLEASH_REFRESH_INTERVAL = new InjectionToken<string>(
  'Unleash refresh interval'
);
export const UNLEASH_TENANT = new InjectionToken<string>('Unleash tenant');
export const UNLEASH_ALWAYS_ENABLE = new InjectionToken<string>(
  'Unleash always enable'
);

@Injectable({
  providedIn: 'root',
})
export class UnleashService extends ToggleService {
  private readonly context: IContext | object = {};
  private initialized = false;
  private unleashClient: UnleashClient;
  public isInitializedSubject = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(UNLEASH_URL) private readonly url: string,
    @Inject(UNLEASH_SECRET) private readonly clientKey: string,
    @Inject(UNLEASH_ENV) private readonly environment: string,
    @Inject(UNLEASH_REFRESH_INTERVAL) private readonly refreshInterval: string,
    @Inject(UNLEASH_TENANT) private readonly tenant: string,
    @Inject(PLATFORM_ID) private readonly platformId: string,
    private readonly logger: Logger
  ) {
    super();
  }

  protected updateContext(field: string, value: string): void {
    this.context[field] = value;
    this.unleashClient.updateContext(this.context);
  }

  public getVariant(
    toggleName: string,
    defaultValue: string | object
  ): string | object {
    if (!this.initialized || !this.unleashClient) {
      this.logger.warn(
        'Unleash has not been initialized, returning default variant value',
        { toggleName, value: defaultValue }
      );
      return defaultValue;
    }

    const variant = this.unleashClient.getVariant(toggleName);
    const type = variant?.payload?.type;
    let value = variant?.payload?.value;
    try {
      if (type === 'json') {
        value = JSON.parse(variant?.payload?.value);
      }
    } catch {
      value = undefined;
    }

    return value ?? defaultValue;
  }

  public async initialize(): Promise<void> {
    if (this.initialized || !isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }

    this.unleashClient = new UnleashClient({
      url: this.url,
      clientKey: this.clientKey,
      appName: UNLEASH_APP,
      environment: this.environment,
      refreshInterval: Number(this.refreshInterval),
      disableMetrics: true,
    });

    try {
      this.updateTenant(this.tenant);

      await this.unleashClient.start();

      this.initialized = true;
      this.isInitializedSubject.next(true);
    } catch (reason) {
      this.logger.warn(
        'Failed to initialize unleash, all features will be disabled to avoid subsequent errors',
        { error: reason }
      );

      return Promise.reject(new Error(reason));
    }
    return Promise.resolve();
  }

  public isEnabled(toggleName: string): boolean {
    if (!this.initialized || !this.unleashClient) {
      this.logger.warn(
        'Unleash has not been initialized, toggles will be disabled',
        { toggleName, enabled: false }
      );
      return false;
    }
    const enabled = this.unleashClient.isEnabled(toggleName);
    return enabled;
  }

  public isInitialized(): Observable<boolean> {
    return this.isInitializedSubject.asObservable();
  }
}
